import {
	Button,
	ClickAwayListener,
	IconButton,
	Paper,
	Tooltip,
	TooltipProps,
	Typography,
	useMediaQuery,
	Modal,
	PaperProps,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import {paletteColors} from "@indebted/theme"
import {styled} from "@mui/material/styles"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CloseIcon from "@mui/icons-material/Close"
import {paymentArrangementAPI} from "@indebted/api/paymentArrangementAPI"
import {useNavigate} from "react-router-dom"
import {useAnalytics} from "@indebted/analytics"
import {Dispatch, ReactNode, useState} from "react"

import {InitiatedDebitByCustomer, ManagePageI18n, ManagePageType} from "../types"
import {nextPage} from "../Navigator"
import {
	CardContentLessPadding,
	CardHeaderLessPadding,
	FailedPaymentTypography,
	GreyStyledCard,
} from "../../../src/Pages/ManagePaymentPlan/styles"

import {ChargeInitiateFailedPage} from "./ChargeInitiateFailed"
import {PaymentArrangementDetailsCard} from "./PaymentArrangementDetailsCard"
import {
	ColoredTypography,
	ColoredTypographyWithBottomMargin,
	CurrencyTypography,
	GradientHeaderGrid,
	GridWithHorizontalPadding,
	PaidAmountTypography,
	StyledTypography,
} from "./styles"

const ManagePaymentArrangement = ({
	PaymentArrangementID,
	I18n,
	InstalmentAmount,
	Currency,
	Frequency,
	TotalPaymentAmount,
	PaymentMethod,
	PaymentMethodDetails,
	NextDueDate,
	PaidAmount,
	EndDate,
	DisableExtraPayment,
	setComponent,
}: ManagePageType & {setComponent: Dispatch<ReactNode>}) => {
	const [open, setOpen] = useState(false)
	const handleTooltipClose = () => {
		setOpen(false)
	}
	const handleTooltipOpen = () => {
		setOpen(true)
	}
	return (
		<Grid container direction="column" alignItems="center">
			<Grid container direction="column" alignItems="stretch" justifyContent="space-between">
				<ManagePaymentArrangementComponentHeaderSection I18n={I18n} />

				<GridWithHorizontalPadding container direction="column">
					<Grid container direction="row" justifyContent="space-between">
						<StyledTypography color={paletteColors.stratosLight}>{I18n.AmountPaidLabel}</StyledTypography>
					</Grid>
					<Grid container direction="row" alignItems="flex-end">
						<PaidAmountTypography color={paletteColors.stratos}>{PaidAmount}</PaidAmountTypography>
						<CurrencyTypography color={paletteColors.stratos}>{Currency}</CurrencyTypography>
						<ClickAwayListener onClickAway={handleTooltipClose}>
							<StyledTooltip
								PopperProps={{
									disablePortal: true,
								}}
								arrow
								placement="top"
								onClose={handleTooltipClose}
								open={open}
								disableFocusListener
								disableHoverListener
								disableTouchListener
								title={<Typography color="inherit">{I18n.AmountPaidTooltip}</Typography>}
							>
								<IconButton
									style={{
										marginBottom: "-3px",
										marginLeft: "-6px",
									}}
									aria-label="close"
									onMouseOver={handleTooltipOpen}
									onMouseLeave={handleTooltipClose}
									onClick={handleTooltipOpen}
									size="large"
								>
									<InfoOutlinedIcon htmlColor="#020452" fontSize="small" />
								</IconButton>
							</StyledTooltip>
						</ClickAwayListener>
					</Grid>
					<Grid container direction="row" justifyContent="space-between">
						<ColoredTypography variant="body2" color={paletteColors.stratosLight}>
							{I18n.AmountRemainingAndPaidInstalmentsText}
						</ColoredTypography>
					</Grid>
					<NextInstalmentCard
						PaymentArrangementID={PaymentArrangementID}
						DisableExtraPayment={DisableExtraPayment}
						I18n={I18n}
						setComponent={setComponent}
					/>
					<PaymentArrangementDetailsCard
						PaymentArrangementID={PaymentArrangementID}
						I18n={I18n}
						InstalmentAmount={InstalmentAmount}
						Frequency={Frequency}
						EndDate={EndDate}
						TotalPaymentAmount={TotalPaymentAmount}
						PaymentMethod={PaymentMethod}
						PaymentMethodDetails={PaymentMethodDetails}
						NextDueDate={NextDueDate}
					/>
					<ColoredTypography
						variant="body2"
						color={paletteColors.stratosLight}
						style={{padding: "20px 5px 0px"}}
					>
						{I18n.EndDateDisclaimerText}
					</ColoredTypography>
				</GridWithHorizontalPadding>
			</Grid>
		</Grid>
	)
}

const NextInstalmentCard = ({
	PaymentArrangementID,
	I18n,
	DisableExtraPayment,
	setComponent,
}: {
	PaymentArrangementID: string
	I18n: ManagePageI18n
	DisableExtraPayment: boolean
	setComponent: Dispatch<ReactNode>
}) => {
	if (DisableExtraPayment) {
		return <></>
	}

	return (
		<GreyStyledCard variant="outlined">
			<CardHeaderLessPadding
				title={
					<Grid container direction="row" alignItems="flex-end" justifyContent="space-between">
						<FailedPaymentTypography color={paletteColors.stratos}>
							{I18n.ExtraPaymentHeading}
						</FailedPaymentTypography>
					</Grid>
				}
			/>
			<CardContentLessPadding>
				<Grid container direction="row" justifyContent="center" style={{marginBottom: "10px"}}>
					<PayInstalmentModal
						PaymentArrangementID={PaymentArrangementID}
						I18n={I18n}
						setComponent={setComponent}
					/>
				</Grid>
			</CardContentLessPadding>
		</GreyStyledCard>
	)
}

const PayInstalmentModal = ({
	PaymentArrangementID,
	I18n,
	setComponent,
}: {
	PaymentArrangementID: string
	I18n: ManagePageI18n
	setComponent: Dispatch<ReactNode>
}) => {
	const [open, setOpen] = useState(false)
	const [buttonDisable, setButtonDisable] = useState(false)
	const [debitId] = useState<string>(crypto.randomUUID())
	const mobile = useMediaQuery("(max-width:599px)")
	const navigate = useNavigate()
	const {revenue} = useAnalytics()
	const handleOpen = () => {
		setOpen(true)
	}
	const handleClose = () => {
		setOpen(false)
	}

	function payNow() {
		setButtonDisable(true)
		paymentArrangementAPI
			.initiateDebit(PaymentArrangementID, debitId)
			.then((result: InitiatedDebitByCustomer) => {
				revenue(I18n.ExtraPaymentModalAmount, I18n.ExtraPaymentModalAmountCurrency, "ExtraInstalment")
				navigate(nextPage(result.Next, PaymentArrangementID))
			})
			.catch(() => {
				setComponent(<ChargeInitiateFailedPage I18n={I18n} />)
			})
	}

	return (
		<>
			<Button
				style={{
					borderRadius: "4px",
					padding: "6px 16px",
					marginTop: "10px",
					width: "100%",
				}}
				onClick={handleOpen}
				type="button"
				variant={"contained"}
			>
				{I18n.ExtraPaymentButtonLabel}
			</Button>
			<Modal open={open} onClose={handleClose} aria-labelledby="modal-title" aria-describedby="modal-description">
				<StyledPaper mobile={mobile}>
					<Grid
						container
						direction="row"
						spacing={3}
						justifyContent="flex-end"
						alignItems="center"
						style={{marginBottom: "10px"}}
					>
						<Grid size={{xs: 9, sm: 10}} style={{paddingLeft: "12px"}}>
							<ColoredTypography
								id="modal-title"
								color={paletteColors.stratosLight2}
								style={{fontSize: "18px", fontWeight: 700}}
							>
								{I18n.ExtraPaymentModalHeading}
							</ColoredTypography>
						</Grid>
						<Grid size={{xs: 3, sm: 2}} style={{display: "flex", justifyContent: "flex-end"}}>
							<IconButton aria-label="close" onClick={handleClose} size="large">
								<CloseIcon htmlColor="rgba(50,50,50,0.45)" />
							</IconButton>
						</Grid>
					</Grid>
					<Grid style={{paddingLeft: "6px"}}>
						<ColoredTypography color={paletteColors.stratosLight2}>
							{I18n.ExtraPaymentModalDescription} <br />
							<br />
							{I18n.ExtraPaymentModalText}
						</ColoredTypography>
					</Grid>
					<Grid
						container
						direction="column"
						justifyContent="center"
						alignItems="center"
						style={{marginTop: "30px"}}
					>
						<Button
							style={{borderRadius: "4px", padding: "6px 16px", marginTop: "10px", width: "100%"}}
							type="button"
							onClick={payNow}
							variant="contained"
							disabled={buttonDisable}
						>
							{I18n.ExtraPaymentModalPayButtonLabel}
						</Button>
						<Grid>
							<Button
								style={{borderRadius: "35px", paddingBottom: "10px"}}
								type="button"
								onClick={handleClose}
								variant="text"
							>
								{I18n.ExtraPaymentModalCancelButtonLabel}
							</Button>
						</Grid>
					</Grid>
				</StyledPaper>
			</Modal>
		</>
	)
}

const ManagePaymentArrangementComponentHeaderSection = ({I18n}: {I18n: ManagePageI18n}) => {
	return (
		<GradientHeaderGrid padded>
			<Grid style={{padding: "12px"}}>
				<ColoredTypographyWithBottomMargin variant="h6" color={paletteColors.stratos} className="amp-mask">
					{I18n.Title}
				</ColoredTypographyWithBottomMargin>
				<ColoredTypography variant="body2" color={paletteColors.stratos} className="amp-mask">
					{I18n.Message}
				</ColoredTypography>
			</Grid>
		</GradientHeaderGrid>
	)
}

const StyledTooltip = styled(({className, ...props}: {className?: string} & TooltipProps) => (
	<Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
	[`& .MuiTooltip-tooltip`]: {
		backgroundColor: "#fff",
		color: "rgba(0, 0, 0, 0.87)",
		maxWidth: 220,
		fontSize: theme.typography.pxToRem(12),
		border: "1px solid lightgray",
		boxShadow: "10px 10px 10px lightgray",
		margin: "0px",
	},
	[`& .MuiTooltip-arrow`]: {
		color: "#f5f5f9",
	},
}))

const StyledPaper = styled(({mobile, ...props}: {mobile: boolean} & PaperProps) => <Paper {...props} />)(
	({theme, mobile}) => ({
		position: "relative",
		width: mobile ? "80%" : "400px",
		top: "50%",
		left: theme.direction === "rtl" ? "auto" : "50%",
		right: theme.direction === "rtl" ? "50%" : "auto",
		transform: theme.direction === "rtl" ? "translate(50%, -50%)" : "translate(-50%, -50%)",
		borderRadius: "24px",
		paddingBottom: "20px",
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[5],
		padding: theme.spacing(4),
		paddingTop: "0px",
		outline: "none",
	}),
)

export {ManagePaymentArrangement}
